import type { JSX } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Tags,
  Link2,
  Boxes,
  Cpu,
  Sparkles,
  Plug,
  BookText,
  FileText,
  GitBranch,
  Wallet,
  ScrollText,
  ShieldCheck,
  Bug,
  MessagesSquare,
  Settings,
  Download,
  Waypoints,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { AdminRole } from '@echo-work-platform/shared-types';
import { useAdminRoles } from '@/auth/admin-role-context';
import { buildCommit, buildVersion, echoProxyBaseUrl } from '@/auth/msal-config';
import { DESKTOP_DOWNLOADS, KNOWLEDGE_BASE, isFeatureEnabled } from '@/auth/features';
import { brandLogoUrl, brandName } from '@/branding/branding';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  roles?: AdminRole[];
  feature?: string;
}

interface NavSection {
  title?: string;
  items: NavItem[];
  roles?: AdminRole[];
}

// Roles absent ⇒ ADMIN-only (matches the server's default-deny). ADMIN is in every set,
// so an ADMIN sees everything; item-level roles override the section for mixed sections.
const sections: NavSection[] = [
  {
    items: [{ to: '/', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    title: 'User Management',
    items: [
      { to: '/users', label: 'Users', icon: Users },
      { to: '/segments', label: 'Segments', icon: Tags },
      { to: '/group-mappings', label: 'Group Mappings', icon: Link2 },
    ],
  },
  {
    title: 'AI Resources',
    roles: ['ADMIN', 'AI_ADMIN'],
    items: [
      { to: '/providers', label: 'Providers', icon: Boxes },
      { to: '/models', label: 'Models', icon: Cpu },
      { to: '/skills', label: 'Agents', icon: Sparkles },
      { to: '/mcps', label: 'Integrations', icon: Plug },
      { to: '/knowledge-bases', label: 'Knowledge Bases', icon: BookText, feature: KNOWLEDGE_BASE },
      { to: '/system-prompt', label: 'System Prompt', icon: FileText },
    ],
  },
  {
    title: 'Permission Management',
    roles: ['ADMIN', 'AI_ADMIN'],
    items: [{ to: '/bindings', label: 'Bindings', icon: GitBranch }],
  },
  {
    title: 'Reporting',
    items: [
      { to: '/costs', label: 'Costs', icon: Wallet },
      { to: '/audit-log', label: 'Audit Log', icon: ScrollText, roles: ['ADMIN', 'AUDITOR'] },
    ],
  },
  {
    title: 'Compliance',
    items: [{ to: '/compliance', label: 'Compliance', icon: ShieldCheck }],
  },
  {
    title: 'Diagnostics',
    roles: ['ADMIN', 'AUDITOR'],
    items: [
      { to: '/error-logs', label: 'Error Logs', icon: Bug },
      { to: '/conversations', label: 'Conversations', icon: MessagesSquare },
    ],
  },
  {
    title: 'Distribution',
    items: [{ to: '/downloads', label: 'Downloads', icon: Download, feature: DESKTOP_DOWNLOADS }],
  },
  {
    title: 'System',
    items: [{ to: '/settings', label: 'Settings', icon: Settings }],
  },
];

const ADMIN_ONLY: AdminRole[] = ['ADMIN'];

// EW-92: the EchoProxy entry exists only when the deployment has an EchoProxy. It sits in the
// Distribution section, so it inherits that section's ADMIN-only visibility.
function buildSections(): NavSection[] {
  if (!echoProxyBaseUrl) return sections;
  return sections.map((s) =>
    s.title === 'Distribution'
      ? { ...s, items: [...s.items, { to: '/echoproxy', label: 'EchoProxy', icon: Waypoints }] }
      : s,
  );
}

export function Sidebar(): JSX.Element {
  const { hasAnyRole } = useAdminRoles();
  const { prefix, suffix } = brandName();
  const logoUrl = brandLogoUrl();
  const visibleSections = buildSections()
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          (item.feature === undefined || isFeatureEnabled(item.feature)) &&
          hasAnyRole(item.roles ?? section.roles ?? ADMIN_ONLY),
      ),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <aside className="flex h-screen w-60 flex-col border-r border-border bg-sidebar">
      <div className="flex h-14 items-center gap-2 border-b border-border px-4">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            className="h-7 w-7 rounded-md object-contain"
            data-testid="sidebar-brand-logo"
          />
        ) : (
          <span
            className="flex h-7 w-7 items-center justify-center rounded-md bg-action text-sm font-bold text-action-foreground"
            data-testid="sidebar-brand-badge"
          >
            E
          </span>
        )}
        <span className="text-lg font-medium text-heading" data-testid="sidebar-brand">
          {prefix}
          <span className="text-primary-600">{suffix}</span>
        </span>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {visibleSections.map((section, i) => (
          <div key={section.title ?? `section-${i}`}>
            {section.title && (
              <p className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {section.title}
              </p>
            )}
            {section.items.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md border-l-2 px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'border-nav-active-border bg-nav-active text-nav-active-foreground'
                      : 'border-transparent text-muted-foreground hover:bg-nav-hover hover:text-foreground',
                  )
                }
              >
                <Icon className={cn('h-4 w-4')} />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div
        className="border-t border-border p-3 text-[10px] text-muted-foreground"
        data-testid="sidebar-build-info"
      >
        {buildVersion ? `v${buildVersion}` : 'dev build'}
        {buildCommit ? ` · ${buildCommit.slice(0, 7)}` : ''}
      </div>
    </aside>
  );
}
